import { Card, Grid, Icon, IconButton, Tooltip, Checkbox, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { post, get, postNoResponse } from "utils/WebService";
import { infoAviso, successAlert, errorAlert } from "utils/Functions";
import CredentialCreateEdit from "./CredentialCreateEdit";

const trfOptions = [
    { value: 'trf1', label: 'TRF1' },
    { value: 'trf2', label: 'TRF2' },
    { value: 'trf3', label: 'TRF3' },
    { value: 'trf4', label: 'TRF4' },
    { value: 'trf5', label: 'TRF5' },
    { value: 'trf6', label: 'TRF6' },
];

export default function CredentialConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: []
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [filtered, setFiltered] = useState([]);
    
    const [filters, setFilters] = useState({
        sistema: null,
        estado: null,
        trf: null,
    });
    const [sistemas, setSistemas] = useState([]);
    const [states, setStates] = useState([]);

    useEffect(() => {
        get(`/sistemas/list-all`).then((res) => {
            setSistemas(res.list || []);
        });
        get(`/addresses/states`).then(({ list }) => {
            setStates(list || []);
        });
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting }, currentFilters = filters) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
        };
        tableState.current = updatedTableState;

        post("/sistema-credencial/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            sistemaId: currentFilters.sistema ? currentFilters.sistema.id : null,
            estadoId: currentFilters.estado ? currentFilters.estado.id : null,
            trf: currentFilters.trf ? currentFilters.trf.value : null,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setRecordsTotal(recordsTotal);
            setData(data);
            setLoadingTable(false);
        }).catch(() => setLoadingTable(false));
    }, []);

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const handleRefresh = () => {
        fetchData({ ...tableState.current });
    };

    const handleApplyFilter = () => {
        fetchData({ ...tableState }, filters);
    };

    const handleCleanFilter = () => {
        const emptyFilters = { sistema: null, estado: null, trf: null };
        setFilters(emptyFilters);
        const updatedTableState = { ...tableState.current, pageIndex: 0 };
        tableState.current = updatedTableState;
        fetchData(updatedTableState, emptyFilters);
    };

    const handleDelete = (id) => {
        infoAviso("Tem certeza que deseja excluir esta credencial?", () => {
            postNoResponse(`/sistema-credencial/delete/${id}`)
                .then(() => {
                    successAlert("Credencial excluída com sucesso!", () => handleRefresh());
                })
                .catch(() => {
                    errorAlert("Erro ao excluir a credencial.");
                });
        }, () => null);
    };

    const columns = [
        {
            header: "Sistema",
            accessorKey: "tbSistema.ccNome",
            cell: info => <DefaultCell>{info.row.original.sistema?.nome || "-"}</DefaultCell>,
            isSorted: false
        },
        {
            header: "UF",
            accessorKey: "tbEstado.ccCodEstado",
            cell: info => <DefaultCell>{info.row.original.estado?.codEstado || "-"}</DefaultCell>,
            isSorted: false
        },
        {
            header: "TRF",
            accessorKey: "ccTrf",
            cell: info => <DefaultCell>{info.row.original.trf ? info.row.original.trf.toUpperCase() : "-"}</DefaultCell>,
            isSorted: false
        },
        {
            header: "Login",
            accessorKey: "login",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Usa Certificado?",
            accessorKey: "usaCertificado",
            cell: info => (
                <Checkbox checked={info.getValue() || false} disabled color="primary" />
            ),
            isSorted: false
        },
        {
            header: "Usa 2FA?",
            accessorKey: "usaAutenticacao2F",
            cell: info => (
                <Checkbox checked={info.getValue() || false} disabled color="primary" />
            ),
            isSorted: false
        },
        {
            header: "Ações",
            isSorted: false,
            enableColumnFilter: false,
            cell: info => (
                <>
                    <Tooltip title="Editar Credencial">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Credencial">
                        <IconButton
                            onClick={() => handleDelete(info.row.original.id)}>
                            <MDTypography variant="h4">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <CredentialCreateEdit
                        credentialId={selectedId}
                        closeCreateEdit={backFunction}
                        refreshTable={handleRefresh} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Sistema/Tribunal</InputLabel>
                            <MDSelect
                                isMulti={false}
                                isClearable={true}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.nome}
                                name="sistema-select-filter"
                                placeholder="Todos"
                                value={filters.sistema}
                                options={sistemas}
                                onChange={selected => setFilters({ ...filters, sistema: selected })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>UF</InputLabel>
                            <MDSelect
                                isMulti={false}
                                isClearable={true}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.codEstado || option.descricao}
                                name="estado-select-filter"
                                placeholder="Todos"
                                value={filters.estado}
                                options={states}
                                onChange={selected => setFilters({ ...filters, estado: selected })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Região (TRF)</InputLabel>
                            <MDSelect
                                isMulti={false}
                                isClearable={true}
                                getOptionValue={option => option.value}
                                getOptionLabel={option => option.label}
                                name="trf-select-filter"
                                placeholder="Todos"
                                value={filters.trf}
                                options={trfOptions}
                                onChange={selected => setFilters({ ...filters, trf: selected })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox>
                                <MDButton size="medium" variant="contained" color="info" onClick={() => {
                                    setSelectedId(null);
                                    setIsCreateEdit(true);
                                }}>
                                    Adicionar Credencial
                                </MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton size="medium" sx={{ marginRight: 2 }} variant="contained" color="info" onClick={handleCleanFilter}>
                                    Limpar Filtro
                                </MDButton>
                                <MDButton size="medium" variant="contained" color="info" onClick={handleApplyFilter}>
                                    Aplicar Filtro
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={columns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    filtered={filtered}
                    setFiltered={setFiltered}
                    tableState={tableState} />
            </Grid>
        </Grid>
    );
}
